import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Lock, Trash2, Plus, X, Sparkles, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { api, track } from "@/lib/api";
import { JOURNAL } from "@/constants/testIds";
import { MicButton } from "@/components/MicButton";

function CrisisNotice({ message, onClose }) {
  return (
    <div data-testid="crisis-notice" className="rounded-2xl border border-[#E4B9B8] bg-[#FBF0EF] p-5 flex items-start gap-3">
      <LifeBuoy className="h-5 w-5 text-[#9E3A3A] shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm text-[#5B2B2B] leading-relaxed">{message}</p>
        <div className="mt-2 flex gap-3">
          <a href="tel:988" className="text-sm font-semibold text-[#9E3A3A] underline">Call or text 988</a>
          <a href="tel:911" className="text-sm font-semibold text-[#9E3A3A] underline">911 (emergency)</a>
        </div>
      </div>
      {onClose && (
        <button onClick={onClose} aria-label="Dismiss" data-testid="crisis-notice-close" className="p-1 rounded-full text-[#9E3A3A]/70 hover:bg-[#F3D9D8]">
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [activePrompt, setActivePrompt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [crisis, setCrisis] = useState(null);
  const [summarizing, setSummarizing] = useState(null);

  const load = () => api.get("/journal").then(({ data }) => setEntries(data)).catch(() => {});

  useEffect(() => {
    track("journal_viewed");
    load();
    api.get("/journal/prompts").then(({ data }) => setPrompts(data)).catch(() => {});
  }, []);

  const startNew = (prompt = null) => {
    setActivePrompt(prompt);
    setTitle(prompt ? prompt.theme : "");
    setBody("");
    setEditing(true);
  };

  const save = async () => {
    if (!body.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/journal", { title, body, prompt_id: activePrompt?.id, prompt_text: activePrompt?.text });
      track("journal_entry_created");
      toast.success("Saved to your private journal.");
      if (data?.crisis) setCrisis(data.crisis_message);
      setEditing(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const summarize = async (id) => {
    setSummarizing(id);
    try {
      const { data } = await api.post(`/journal/${id}/summary`);
      setEntries((es) => es.map((e) => (e.entry_id === id ? { ...e, ai_summary: data.ai_summary } : e)));
      if (data?.crisis) setCrisis(data.crisis_message);
    } catch {
      toast.error("Couldn't create a summary right now. Please try again.");
    } finally {
      setSummarizing(null);
    }
  };

  const remove = async (id) => {
    await api.delete(`/journal/${id}`);
    load();
  };

  if (editing) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-3xl font-semibold">New entry</h1>
          <button onClick={() => setEditing(false)} aria-label="Close" className="p-2 rounded-full hover:bg-[#F7F2EA]">
            <X className="h-5 w-5" />
          </button>
        </div>
        {activePrompt && (
          <div className="bg-[#352238] text-[#FCFAF6] rounded-2xl p-5 mb-5">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">{activePrompt.theme}</p>
            <p className="font-display text-lg mt-2">{activePrompt.text}</p>
          </div>
        )}
        <input data-testid={JOURNAL.titleInput} value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="Title (optional)"
          className="w-full bg-transparent font-display text-2xl font-semibold border-b border-[#E5DED4] pb-3 mb-4 focus:outline-none focus:border-[#C89B55]" />
        <div className="relative">
          <textarea data-testid={JOURNAL.bodyInput} value={body} onChange={(e) => setBody(e.target.value)} autoFocus
            placeholder="Write freely, or tap the mic to speak. This space is entirely yours."
            className="w-full min-h-[320px] bg-[#FCFAF6] border border-[#E5DED4] rounded-2xl p-5 pr-16 leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#C89B55]" />
          <MicButton testid="journal-dictate" className="absolute bottom-4 right-4 h-11 w-11"
            onAppend={(t) => setBody((b) => (b ? b + " " + t : t))} />
        </div>
        <div className="flex items-center justify-between mt-5">
          <span className="text-sm text-[#8A838E] flex items-center gap-1.5"><Lock className="h-4 w-4" /> Private to you</span>
          <button data-testid={JOURNAL.saveButton} onClick={save} disabled={saving || !body.trim()}
            className="rounded-full bg-[#291B2D] text-[#FCFAF6] hover:bg-[#352238] px-8 py-3 font-medium disabled:opacity-40 transition-all">
            {saving ? "Saving…" : "Save entry"}
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="space-y-8">
      {crisis && <CrisisNotice message={crisis} onClose={() => setCrisis(null)} />}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">Journal</p>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold mt-1">Your private space</h1>
          <p className="text-[#59535B] mt-2 flex items-center gap-1.5"><Lock className="h-4 w-4" /> Only you can see what you write here.</p>
        </div>
        <button data-testid={JOURNAL.newButton} onClick={() => startNew()}
          className="rounded-full bg-[#C89B55] text-[#291B2D] hover:bg-[#B38743] px-6 py-3 font-semibold flex items-center gap-2 transition-all">
          <Plus className="h-4 w-4" /> New entry
        </button>
      </div>

      <div>
        <h2 className="font-display text-xl font-semibold mb-3">Guided prompts</h2>
        <div className="flex gap-3 overflow-x-auto sh-scroll pb-2">
          {prompts.map((p) => (
            <button key={p.id} data-testid={JOURNAL.promptChip} onClick={() => startNew(p)}
              className="shrink-0 w-64 text-left bg-[#F7F2EA] border border-[#E5DED4] hover:border-[#C89B55] rounded-2xl p-5 transition-all">
              <p className="text-xs uppercase tracking-wider text-[#B97978] font-semibold">{p.theme}</p>
              <p className="text-sm text-[#252326] mt-2 leading-relaxed line-clamp-3">{p.text}</p>
            </button>
          ))}
        </div>
      </div>

      <div>
        <h2 className="font-display text-xl font-semibold mb-3">Your entries</h2>
        {entries.length === 0 ? (
          <div className="bg-[#F7F2EA] border border-[#E5DED4] rounded-2xl p-10 text-center">
            <p className="text-[#59535B]">Nothing here yet. Start with a prompt or write freely.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {entries.map((e) => (
              <div key={e.entry_id} data-testid={JOURNAL.entryCard}
                className="bg-white border border-[#E5DED4] rounded-2xl p-6 group">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {e.title && <h3 className="font-display text-lg font-semibold">{e.title}</h3>}
                    <p className="text-xs text-[#8A838E] mt-0.5">{new Date(e.created_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</p>
                    <p className="text-[#252326] mt-3 leading-relaxed whitespace-pre-wrap line-clamp-4">{e.body}</p>
                  </div>
                  <button data-testid={`${JOURNAL.deleteButton}-${e.entry_id}`} onClick={() => remove(e.entry_id)}
                    aria-label="Delete entry"
                    className="p-2 rounded-full text-[#8A838E] hover:text-[#9E3A3A] hover:bg-[#FAF0F0] transition-all">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                {e.ai_summary ? (
                  <div data-testid={`journal-summary-${e.entry_id}`} className="mt-4 rounded-xl bg-[#F7F2EA] border border-[#E5DED4] p-4">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#C89B55] font-semibold flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5" /> Reflection
                    </p>
                    <p className="text-sm text-[#3a3340] mt-2 leading-relaxed">{e.ai_summary}</p>
                  </div>
                ) : (
                  <button data-testid={`journal-summarize-${e.entry_id}`} onClick={() => summarize(e.entry_id)}
                    disabled={summarizing === e.entry_id}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#291B2D] rounded-full bg-[#F7F2EA] hover:bg-[#EFE7DA] px-4 py-2 transition-all disabled:opacity-50">
                    <Sparkles className="h-4 w-4 text-[#C89B55]" />
                    {summarizing === e.entry_id ? "Reflecting…" : "AI summary"}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
